import PropTypes from 'prop-types';
import { Avatar, Box, Card, Divider, Grid, List, ListItem, ListItemAvatar, ListItemText, Stack, Typography } from '@mui/material';
import Iconify from '../../../components/Iconify';
import Label from '../../../components/Label';
import { fDateTime } from '../../../utils/formatTime';
import DetailRow from './DetailRow';
import { EmptyModuleState, LoadingPanel, SectionError } from './ManageUserDialogStates';
import WalletSummaryCard from './WalletSummaryCard';
import { formatWalletAmount, getAccountStateColor } from './utils';

export default function ProfileOverviewTab({ state }) {
  const devices = state.devices || [];
  const wallet = state.wallet || {};
  const previewDevices = devices.slice(0, 4);

  if (state.loading && !state.loadComplete) {
    return <LoadingPanel />;
  }

  if (state.error && !state.loadComplete) {
    return <SectionError message={state.error} />;
  }

  return (
    <Stack spacing={3}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <WalletSummaryCard
            title="Fiat Wallet"
            icon="solar:wallet-money-bold"
            subtitle={wallet.currency || 'Fiat balance'}
            primaryLabel="Available balance"
            primaryValue={formatWalletAmount(wallet.balance, wallet.currency)}
            secondaryLabel="Ledger balance"
            secondaryValue={formatWalletAmount(wallet.ledger_balance, wallet.currency)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <WalletSummaryCard
            title="USDT Wallet"
            icon="solar:bitcoin-card-bold"
            subtitle="Stablecoin balance"
            primaryLabel="Available balance"
            primaryValue={formatWalletAmount(wallet.usdt_balance, 'USDT')}
            secondaryLabel="Ledger balance"
            secondaryValue={formatWalletAmount(wallet.usdt_ledger_balance, 'USDT')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
            <Stack spacing={2} sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'text.primary',
                    bgcolor: 'grey.100',
                  }}
                >
                  <Iconify icon="solar:user-id-bold" width={20} height={20} />
                </Box>
                <Box>
                  <Typography variant="h6">About User</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Personal information details.
                  </Typography>
                </Box>
              </Stack>

              <Divider />

              <Stack spacing={1.5}>
                <DetailRow
                  label="Display name"
                  value={state.user.display_name || `${state.user.first_name || ''} ${state.user.last_name || ''}`.trim()}
                />
                <DetailRow label="User ID" value={state.user.user_id} />
                <DetailRow
                  label="Account state"
                  valueNode={<Label color={getAccountStateColor(state.account_status)}>{state.account_status || 'unknown'}</Label>}
                />
                <DetailRow label="Verification" value={state.user.account_verification_status} />
                <DetailRow label="Email" value={state.user.email} />
                <DetailRow label="Phone" value={state.user.phone} />
                <DetailRow label="Gender" value={state.user.gender} />
                <DetailRow label="Date of birth" value={state.user.dob} />
                <DetailRow label="Created at" value={state.user.created_at ? fDateTime(state.user.created_at) : 'N/A'} />
              </Stack>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
            <Stack spacing={2} sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'text.primary',
                    bgcolor: 'grey.100',
                  }}
                >
                  <Iconify icon="solar:smartphone-2-bold" width={20} height={20} />
                </Box>
                <Box>
                  <Typography variant="h6">Device Snapshot</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recent device observations from the current user details payload.
                  </Typography>
                </Box>
              </Stack>

              <Divider />

              {previewDevices.length ? (
                <List disablePadding>
                  {previewDevices.map((device, index) => (
                    <Box key={device.device_id || `${device.name || 'device'}-${index}`}>
                      <ListItem disableGutters sx={{ py: 1.25 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: 'grey.100',
                              color: 'text.primary',
                            }}
                          >
                            <Iconify icon="solar:smartphone-2-bold" width={18} height={18} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={device.device_model || device.device_id || 'Unknown device'}
                          secondary={
                            [
                              device.manufacturer,
                              device.platform ? `${device.platform} ${device.os_version || ''}`.trim() : null,
                              device.app_version ? `v${device.app_version}` : null,
                            ]
                              .filter(Boolean)
                              .join(' • ') || 'No device metadata'
                          }
                        />
                      </ListItem>
                      {index < previewDevices.length - 1 ? <Divider /> : null}
                    </Box>
                  ))}
                </List>
              ) : (
                <EmptyModuleState
                  title="No devices found"
                  description="The current user details payload returned no device records."
                  icon="solar:smartphone-2-bold"
                />
              )}
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}

ProfileOverviewTab.propTypes = {
  state: PropTypes.object.isRequired,
};
